import { DateInput, Field, NumericInput, TextOutput, TimeInput } from 'Components/Core';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { Grid } from 'Components/Layout';
import { actions } from 'Data/ReduxViewModels/NuclearGaugeStandardisation';
import { hasNonEmptyValue, valueOrDefault } from 'Lib/Any';
import { FormatTypes } from 'Lib/Format';
import { DateTimeDataFormat } from 'Lib/Format/types';
import { pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import EquipmentGridFieldSection from '../Core/EquipmentGridFieldSection';
import { IWithStandardisationInjectedProps } from './Core';
import {
  IStandardisationRecords as IStandardisationRecord,
  NuclearGaugeStandardizationType,
  dualPurposeHeader,
  moistureDensityHeader,
  thinLiftHeader,
} from './types';

interface IStandardisationRecordSection extends IWithStandardisationInjectedProps {
  namePrefix?: string;
}

let StandardizationRecordSection: React.FC<IStandardisationRecordSection> = ({
  namePrefix,
  createStandardisationSelector,
}) => {
  const dispatch = useDispatch();
  const [showError, setshowError] = useState(false);
  // We don't currently have 'permissions' setup in the JWT Claims so in order to make the worksheet `readonly` we look
  // for the existence of the Id and use that to determine if a model with data was returned.
  const hasDataId = useSelector(pipe(createStandardisationSelector!('Id'), (id) => hasNonEmptyValue(id)));
  const records = useSelector(
    pipe(createStandardisationSelector!('Records'), valueOrDefault([]), (records) =>
      records
        .filter((r: IStandardisationRecord) => !r.IsDeleted)
        .map(
          (p: any, i: number) =>
            ({
              DensityCountInvalid: p?.DensityCountInvalid ?? false,
              MoistureCountInvalid: p?.MoistureCountInvalid ?? false,
              DensityCountInvalid2: p?.DensityCountInvalid2 ?? false,
              Locked: i !== records.length - 1 && (p?.IsComplete ?? false),
              DateTime: p?.Date,
            }) as IStandardisationRecord
        )
    ),
    deepEquals
  );

  //Get Gauge Type
  const nuclearGaugeType = useSelector(
    pipe(
      createStandardisationSelector!('NuclearGaugeType'),
      valueOrDefault(NuclearGaugeStandardizationType.MoistureDensity)
    )
  );
  const handleGridCreateRow = () => {
    dispatch(actions.addArrayEntry({ name: `${namePrefix}.Records`, data: {}, index: records.length, sync: true }));
  };
  const handleGridRemoveRow = (index: number) => {
    dispatch(actions.setValue({ name: `${namePrefix}.Records[${index}].IsDeleted`, value: true, sync: true }));
  };

  //Func to get Header value
  const getColumnHeaderValue = (gaugeType: NuclearGaugeStandardizationType) => {
    switch (gaugeType) {
      case NuclearGaugeStandardizationType.MoistureDensity:
        return moistureDensityHeader;
      case NuclearGaugeStandardizationType.ThinLift:
        return thinLiftHeader;
      case NuclearGaugeStandardizationType.DualPurpose:
        return dualPurposeHeader;
      default:
        return [];
    }
  };

  //Gets the latest inactive row's date. To use as a minimum limit for the date input.
  const getLatestInactiveRowDate = (records: IStandardisationRecord[]) => {
    const lastInactiveRecord = records
      .slice()
      .reverse()
      .find((r) => r.Locked);
    return lastInactiveRecord?.DateTime;
  };

  const getLatestInactiveRowTime = useCallback((latestInactiveDate?: string, currentSelectedDate?: string) => {
    if (!latestInactiveDate || !currentSelectedDate) return undefined;
    //We only want to enforce a min time if the current selected date, is equal to the latest inactive date.
    if (new Date(latestInactiveDate).toDateString() !== new Date(currentSelectedDate).toDateString()) {
      return undefined;
    }
    return parseDateTimeToTime(latestInactiveDate);
  }, []);

  const parseDateTimeToTime = (dateTime: string) => {
    const date = new Date(dateTime);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const handleTimeChange = (e: any, recordDateTime: string, index: number, latestInactiveDate?: string) => {
    let submittedTime = e.target.value;
    let minTime = getLatestInactiveRowTime(latestInactiveDate, recordDateTime);

    //Only submit the value if it's greater than the min time.
    let submitValue = submittedTime > minTime!;

    // Split the original DateTime string to extract the date part and the timezone (if any)
    const [datePart] = recordDateTime.split('T');

    // Replace the time portion while keeping the date.
    const newDateTimeString = `${datePart}T${submittedTime}:00.000`;

    //Submit the result, if the new value is not less than the min time.
    dispatch(actions.setValue({ name: `data.Records[${index}].Date`, value: newDateTimeString, sync: submitValue }));
  };

  let latestInactiveDate = getLatestInactiveRowDate(records);

  useEffect(() => {
    //for any active record, check if the date time is greater than or equal to the date time of the latest inactive row
    records.some((record: IStandardisationRecord, i: number) => {
      let latestInactiveRowTime = getLatestInactiveRowTime(latestInactiveDate, record.DateTime);
      if (latestInactiveRowTime === undefined || record.Locked) {
        setshowError(false);
        return false;
      }
      let invalid = parseDateTimeToTime(record.DateTime) < latestInactiveRowTime;
      setshowError(invalid);
      return invalid;
    });
  }, [records, latestInactiveDate, getLatestInactiveRowTime]);

  return (
    <>
      <EquipmentGridFieldSection
        heading={`Standardizations`}
        name="Records"
        messages={
          showError
            ? [{ message: "Time must be greater than the previous inactive row's time", severity: 'error' }]
            : []
        }
        stateSelector={createStandardisationSelector!}
      >
        <Grid
          disabled={false}
          colHeaders={getColumnHeaderValue(nuclearGaugeType)}
          hiddenCols={
            nuclearGaugeType === NuclearGaugeStandardizationType.ThinLift
              ? [4, 5, 8, 9]
              : nuclearGaugeType === NuclearGaugeStandardizationType.DualPurpose
              ? [4, 5]
              : [6, 7, 8, 9]
          }
          data={records.map((record: IStandardisationRecord, i: number) => [
            <Field
              input={record.Locked ? <TextOutput /> : <DateInput />}
              dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime}
              min={latestInactiveDate}
              format={{ type: FormatTypes.Date }}
              name={`Records[${i}].Date`}
            />,
            <Field
              input={record.Locked ? <TextOutput /> : <TimeInput />}
              dataFormat={DateTimeDataFormat.UnspecifiedLocalDateTime}
              format={{ pattern: 'hh:mm A', type: FormatTypes.Time }}
              value={record?.DateTime}
              onChange={(e: any) => handleTimeChange(e, record?.DateTime, i, latestInactiveDate)}
              min={getLatestInactiveRowTime(latestInactiveDate, records[i].DateTime)}
            />,
            //Density/Density1/System1
            <Field input={<TextOutput />} name={`Records[${i}].Last4DensityAverage`} />,
            <Field
              input={record.Locked ? <TextOutput /> : <NumericInput />}
              name={`Records[${i}].DensityCount`}
              severity={records[i].DensityCountInvalid ? 'error' : undefined}
            />,
            //Moisture_density
            <Field
              input={<TextOutput />}
              name={`Records[${i}].Last4MoistureAverage`}
              visible={nuclearGaugeType === NuclearGaugeStandardizationType.MoistureDensity}
            />,
            <Field
              input={record.Locked ? <TextOutput /> : <NumericInput />}
              name={`Records[${i}].MoistureCount`}
              severity={records[i].MoistureCountInvalid ? 'error' : undefined}
              visible={nuclearGaugeType === NuclearGaugeStandardizationType.MoistureDensity}
            />,
            //Thin_lift && DualPurpose
            <Field
              input={<TextOutput />}
              name={`Records[${i}].Last4Density2Average`}
              visible={
                nuclearGaugeType === NuclearGaugeStandardizationType.ThinLift ||
                nuclearGaugeType === NuclearGaugeStandardizationType.DualPurpose
              }
            />,
            <Field
              input={record.Locked ? <TextOutput /> : <NumericInput />}
              name={`Records[${i}].DensityCount2`}
              severity={records[i].DensityCountInvalid2 ? 'error' : undefined}
              visible={
                nuclearGaugeType === NuclearGaugeStandardizationType.ThinLift ||
                nuclearGaugeType === NuclearGaugeStandardizationType.DualPurpose
              }
            />,
            //Dual Purpose
            <Field
              input={<TextOutput />}
              name={`Records[${i}].Last4MoistureAverage`}
              visible={nuclearGaugeType === NuclearGaugeStandardizationType.DualPurpose}
            />,
            <Field
              input={record.Locked ? <TextOutput /> : <NumericInput />}
              name={`Records[${i}].MoistureCount`}
              severity={records[i]?.MoistureCountInvalid ? 'error' : undefined}
              visible={nuclearGaugeType === NuclearGaugeStandardizationType.DualPurpose}
            />,
          ])}
          rows={records.map((_: any, i: number) => ({
            preventRemoveRow: records[i].Locked && i < records.length - 1,
          }))}
          onCreateRow={hasDataId ? handleGridCreateRow : undefined}
          onRemoveRow={handleGridRemoveRow}
        ></Grid>
      </EquipmentGridFieldSection>
    </>
  );
};

StandardizationRecordSection = withInjectedProps(StandardizationRecordSection);

export default StandardizationRecordSection;
export { StandardizationRecordSection };
